// =============================================================================
// varietyFamily / varietyMatches — fold a run variety or a rate-card product's
// fruit_type to a "commodity family" so one product can serve every spelling
// and cultivar of a fruit.
//
// Why this exists:
//   The pallet variant pickers (Pallets.tsx, PalletDetail.tsx) scope a pack's
//   variants to the run's variety by matching financial_products.fruit_type
//   against runs.variety. That match used to be exact (case-insensitive). But a
//   commodity like LEMON is captured on runs under several variety strings —
//   "LEMONS", "LEMON", "EUREKA LEMON", "LISBON LEMON" — that all pack into the
//   same A15C lemon boxes (there is one lemon product set, not one per
//   cultivar). Exact matching hid the lemon products from the cultivar-spelled
//   runs (e.g. the active 127-pallet "LEMON" run), so packers couldn't pick a
//   product. Folding to a family fixes the match while keeping the cultivar
//   name on the run for traceability.
//
//   A NULL/blank fruit_type stays a genuine wildcard (a generic pack offered on
//   every run) — see markVariantResolver.ts for the brand step that runs AFTER
//   this variety filter. Tagging discipline (every variety-specific row carries
//   a fruit_type) is what keeps wildcards from leaking; see mig 857.
//
// Fold rules are DATA (variety_family_rules, mig 1039), read via the
// useVarietyFamilies() hook (src/lib/useVarietyFamilies.ts) and passed in here.
// Soft-citrus cultivars stay DISTINCT on purpose — folding them to their
// commodity would re-introduce the mig-857/858 Nova-on-Tango leak, so new fold
// rules must be added deliberately, never by commodity. The default below (no
// rules supplied) reproduces the original single hardcoded rule, so direct
// callers, tests, and the offline / empty-cache path are unchanged.
// =============================================================================

export interface VarietyFamilyRule {
  // Case-insensitive substring matched against the (lower-cased) variety /
  // fruit_type. First matching rule (in the supplied order) wins.
  token:  string
  family: string
}

// The original hardcoded behaviour, as data: any '%lemon%' -> 'lemon' family.
// Used when no rules are supplied (direct callers / tests / offline fallback).
export const DEFAULT_VARIETY_FAMILY_RULES: VarietyFamilyRule[] = [
  { token: 'lemon', family: 'lemon' },
]

export function varietyFamily(
  v: string | null | undefined,
  rules: VarietyFamilyRule[] = DEFAULT_VARIETY_FAMILY_RULES,
): string {
  const s = (v ?? '').trim().toLowerCase()
  if (!s) return ''
  for (const r of rules) {
    if (s.includes(r.token.trim().toLowerCase())) return r.family.trim().toLowerCase()
  }
  return s
}

// A product tagged `fruitType` is offered on a run of `runVariety` when the
// product is an unscoped generic (blank fruit_type) or their families match.
export function varietyMatches(
  fruitType:  string | null | undefined,
  runVariety: string | null | undefined,
  rules: VarietyFamilyRule[] = DEFAULT_VARIETY_FAMILY_RULES,
): boolean {
  const ft = (fruitType ?? '').trim()
  if (!ft) return true
  return varietyFamily(ft, rules) === varietyFamily(runVariety, rules)
}

// The variant-picker match: a product is offered on a run when its fruit_type
// matches the run's VARIETY (cultivar-scoped tag, the original rule) OR the
// run's COMMODITY (commodity-scoped tag = "any run of this commodity").
//
// The commodity leg exists because the Rate Card fruit-type picker suggests
// commodity names (STRAWBERRIES) while runs are captured under cultivars
// (YASMIN) — without it, a commodity-tagged catalogue never matches any run
// (Anchor onboarding, 2026-07-14). Cultivar tags (NOVA, TANGO) are unaffected:
// a cultivar never equals a run commodity (SOFT CITRUS), so the deliberate
// soft-citrus separation (mig 857/858) is preserved. A blank runCommodity
// contributes nothing — legacy runs fall back to variety-only matching.
export function fruitTypeMatchesRun(
  fruitType:    string | null | undefined,
  runVariety:   string | null | undefined,
  runCommodity: string | null | undefined,
  rules: VarietyFamilyRule[] = DEFAULT_VARIETY_FAMILY_RULES,
): boolean {
  if (varietyMatches(fruitType, runVariety, rules)) return true
  const commodity = (runCommodity ?? '').trim()
  return !!commodity && varietyMatches(fruitType, commodity, rules)
}
